// src/App.js
import React, { useState, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { Network } from "@capacitor/network";

import Home from "./pages/Home";
import Layout from "./components/Layout";
import Contact from "./pages/Contact";
import OurStore from "./pages/OurStore";
import Whishlist from "./pages/Whishlist";
import Login from "./pages/Login";
import Forgotpassword from "./pages/Forgotpassword";
import Signup from "./pages/Signup";
import Resetpassword from "./pages/Resetpassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import RefundPolicy from "./pages/RefundPolicy";
import ShippingPolicy from "./pages/ShippingPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import SingleProduct from "./pages/SingleProduct";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import ReceiptPage from "./components/Receipt";
import PrintOrder from "./pages/PrintOrder";
import Orders from "./pages/Orders";
import Profile from "./pages/Profile";
import Help from "./pages/Help";
import ScrollToTop from "./components/ScrollToTop";
import Identify from "./pages/Identify";

import { GoogleOAuthProvider } from "@react-oauth/google";

import OfflineOverlay from "./components/OfflineOverlay";
import PriceList from "./pages/PriceList";
import VerificationCode from "./pages/VerificationCode";
import ProfileDetails from "./pages/ProfileDetails";
import ResetPasswordVerify from "./pages/ResetPasswordVerify";

import { PrivateRoutes } from "./routing/PrivateRoutes";
import { OpenRoutes } from "./routing/OpenRoutes";

import ViewOrders from "./pages/ViewOrders";
import PaymentSuccess from "./pages/PaymentSuccess";

import ThankYou from "./pages/ThankYou";
import AppSplash from "./components/AppSplash";

const App = () => {
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const [isOffline, setIsOffline] = useState(false);

  const isNativeAndroid =
    Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";

  const [showAppSplash, setShowAppSplash] = useState(isNativeAndroid);

  useEffect(() => {
    if (!isNativeAndroid) return;

    let hideNativeTimer;
    let appSplashTimer;

    hideNativeTimer = setTimeout(async () => {
      try {
        await SplashScreen.hide();
      } catch (error) {
        console.log("Splash hide error:", error);
      }
    }, 0);

    appSplashTimer = setTimeout(() => {
      setShowAppSplash(false);
    }, 2300);

    return () => {
      clearTimeout(hideNativeTimer);
      clearTimeout(appSplashTimer);
    };
  }, [isNativeAndroid]);

  useEffect(() => {
    if (!isNativeAndroid) return;

    const setupBackButton = async () => {
      const listener = await CapacitorApp.addListener("backButton", () => {
        if (window.history.length > 1) {
          window.history.back();
        } else {
          CapacitorApp.exitApp();
        }
      });

      return listener;
    };

    let listenerHandle;

    setupBackButton().then((handle) => {
      listenerHandle = handle;
    });

    return () => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, [isNativeAndroid]);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, [location]);

  useEffect(() => {
    let listenerHandle = null;

    const setupNetwork = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          const status = await Network.getStatus();
          setIsOffline(!status.connected);

          listenerHandle = await Network.addListener(
            "networkStatusChange",
            (status) => {
              setIsOffline(!status.connected);
            }
          );
          return;
        } catch (error) {
          console.log(
            "Capacitor Network plugin failed, falling back to browser events",
            error
          );
        }
      }

      const handleOffline = () => setIsOffline(true);
      const handleOnline = () => setIsOffline(false);

      setIsOffline(typeof navigator !== "undefined" ? !navigator.onLine : false);

      window.addEventListener("offline", handleOffline);
      window.addEventListener("online", handleOnline);

      listenerHandle = {
        remove: async () => {
          window.removeEventListener("offline", handleOffline);
          window.removeEventListener("online", handleOnline);
        },
      };
    };

    setupNetwork();

    return () => {
      if (listenerHandle?.remove) {
        listenerHandle.remove();
      }
    };
  }, []);

  const handleRetry = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        const status = await Network.getStatus();

        if (status.connected) {
          setIsOffline(false);
          window.location.reload();
        } else {
          setIsOffline(true);
        }
        return;
      }

      if (navigator.onLine) {
        setIsOffline(false);
        window.location.reload();
      } else {
        setIsOffline(true);
      }
    } catch (error) {
      if (navigator.onLine) {
        setIsOffline(false);
        window.location.reload();
      } else {
        setIsOffline(true);
      }
    }
  };

  useEffect(() => {
    document.body.style.overflow = isOffline ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOffline]);

  if (showAppSplash) {
    return <AppSplash />;
  }

  return (
    <>
      <ScrollToTop />
      {isOffline && <OfflineOverlay onRetry={handleRetry} />}

      <Routes>
        <Route
          path="/"
          element={
            <Layout
              isOffline={isOffline}
              onRetry={handleRetry}
              isLoading={isLoading}
            />
          }
        >
          <Route index element={<Home />} />

          <Route
            path="whishlist"
            element={
              <PrivateRoutes>
                <Whishlist />
              </PrivateRoutes>
            }
          />

          <Route path="contact" element={<Contact />} />
          <Route path="print" element={<PrintOrder />} />
          <Route path="product" element={<OurStore />} />
          <Route path="category/:category" element={<OurStore />} />
          <Route path="product/:slug" element={<SingleProduct />} />

          <Route path="identification" element={<Identify />} />
          <Route path="verification-code" element={<VerificationCode />} />

          <Route
            path="login"
            element={
              <OpenRoutes>
                <Login />
              </OpenRoutes>
            }
          />

          <Route
            path="signup"
            element={
              <OpenRoutes>
                <Signup />
              </OpenRoutes>
            }
          />

          <Route
            path="auth/login"
            element={
              <OpenRoutes>
                <Login />
              </OpenRoutes>
            }
          />

          <Route
            path="auth/signup"
            element={
              <OpenRoutes>
                <Signup />
              </OpenRoutes>
            }
          />

          <Route path="forgot-password" element={<Forgotpassword />} />
          <Route path="profile-details" element={<ProfileDetails />} />

          <Route path="reset-password" element={<ResetPasswordVerify />} />
          <Route path="reset-password/:token" element={<Resetpassword />} />
          <Route path="verify-reset-code" element={<ResetPasswordVerify />} />

          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="refund-policy" element={<RefundPolicy />} />
          <Route path="help" element={<Help />} />
          <Route path="price-list" element={<PriceList />} />
          <Route path="shipping-policy" element={<ShippingPolicy />} />
          <Route path="payment-success" element={<PaymentSuccess />} />
          <Route path="thank-you" element={<ThankYou />} />
          <Route path="terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="receipt" element={<ReceiptPage />} />
          <Route path="cart" element={<Cart />} />

          <Route
            path="my-orders"
            element={
              <PrivateRoutes>
                <Orders />
              </PrivateRoutes>
            }
          />

          <Route
            path="my-orders/:id"
            element={
              <PrivateRoutes>
                <ViewOrders />
              </PrivateRoutes>
            }
          />

          <Route
            path="my-profile"
            element={
              <PrivateRoutes>
                <Profile />
              </PrivateRoutes>
            }
          />

          <Route
            path="checkout"
            element={
              <PrivateRoutes>
                <Checkout />
              </PrivateRoutes>
            }
          />

          
        </Route>
      </Routes>
    </>
  );
};

const RootApp = () => {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const isNativeAndroid =
    Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";

  if (isNativeAndroid || !googleClientId) {
    return (
      <HelmetProvider>
        <Router>
          <App />
        </Router>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      <GoogleOAuthProvider clientId={googleClientId}>
        <Router>
          <App />
        </Router>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
};

export default RootApp;


